import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { catchError, Observable, from, switchMap, finalize, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { LoadingService } from '../loading/loading.service';
import { environment } from '../../../../environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private readonly authService: AuthService,
    private readonly loadingService: LoadingService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const skipLoading = req.headers.has('X-Skip-Loading');
    let finalReq = req;

    if (skipLoading) {
      finalReq = req.clone({
        headers: req.headers.delete('X-Skip-Loading'),
      });
    } else {
      this.loadingService.show();
    }

    return from(this.authService.userSession()).pipe(
      switchMap((session) => {
        let authReq = finalReq;
        const token = session?.tokens?.idToken?.toString();

        // Only send the Authorization header to our own API backend
        // This prevents leaking Cognito tokens to 3rd party APIs and prevents S3 signature errors on pre-signed URL uploads
        const isApiRequest = finalReq.url.startsWith(environment.apiUrl);

        if (token && isApiRequest) {
          authReq = finalReq.clone({
            headers: finalReq.headers.set('Authorization', token),
          });
        }

        return next.handle(authReq).pipe(
          catchError((error) => {
            if (error instanceof HttpErrorResponse && error.status === 401 && isApiRequest) {
              // Attempt a token refresh before signing out
              return from(this.authService.cognitoService.forceRefreshSession()).pipe(
                switchMap(() => from(this.authService.userSession())),
                switchMap((newSession) => {
                  const newToken = newSession?.tokens?.idToken?.toString();
                  if (newToken) {
                    const retryReq = finalReq.clone({
                      headers: finalReq.headers.set('Authorization', newToken),
                    });
                    return next.handle(retryReq);
                  }
                  this.authService.signOut().subscribe();
                  return throwError(() => error);
                }),
                catchError(() => {
                  this.authService.signOut().subscribe();
                  return throwError(() => error);
                }),
              );
            }
            return throwError(() => error);
          }),
        );
      }),
      catchError((error) => {
        // Do not forward unauthenticated requests on session fetch failure
        return throwError(() => error);
      }),
      finalize(() => {
        if (!skipLoading) {
          this.loadingService.hide();
        }
      }),
    );
  }
}
